import { devtools, persist } from 'zustand/middleware';

import { validateAuthTokens } from '@pkg/hooks/authentication/utilities';

import { type Token } from '@app/client';
import { create } from '@app/store/zustand/helpers/create';

type AuthTokensState = {
    token: Token;
    hasPassword: boolean;
    isAuthenticated: boolean;
    currentUserId: string | undefined;
};

interface AuthTokensStateStore extends AuthTokensState {
    setAuthTokens: (tokensState: Token, options?: { keepPrevious?: boolean }) => void;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    setHasPassword: (hasPassword: boolean) => void;
    setRefreshToken: (refreshToken: string) => void;
    setCurrentUserId: (currentUserId: string) => void;
}

const initialState: AuthTokensState = {
    token: {
        access_token: '',
        refresh_token: '',
        requires_2fa: undefined,
        has_password: undefined,
    },
    currentUserId: undefined,
    isAuthenticated: false,
    hasPassword: false,
};

export const useAuthTokens = create<AuthTokensStateStore>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,
                setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
                setHasPassword: (hasPassword) => set({ hasPassword }),
                setAuthTokens: (authTokens, options) => {
                    set((currState) => {
                        if (options?.keepPrevious) return { token: { ...currState.token, ...authTokens } };
                        return { token: { ...authTokens } };
                    });
                    validateAuthTokens();
                },
                setRefreshToken: (refreshToken) =>
                    set({ token: { ...initialState.token, refresh_token: refreshToken } }),
                setCurrentUserId: (currentUserId) => set({ currentUserId }),
            }),
            { name: 'tokens' },
        ),
    ),
);

export function setTokens(tokens: Token, options?: { keepPrevious?: boolean }) {
    useAuthTokens.getState().setAuthTokens(tokens, options);
}

type HandleRemoveAuthTokensProps = {
    keepRefreshToken?: boolean;
};
export const handleRemoveAuthTokens = (props?: HandleRemoveAuthTokensProps) => {
    const { keepRefreshToken } = props || {};
    const state = { ...initialState };
    if (keepRefreshToken) {
        state.token.refresh_token = useAuthTokens.getState().token.refresh_token;
    }

    useAuthTokens.setState(state);
};
